import React, { Suspense, lazy, useState, useEffect } from "react";
import * as Sentry from "@sentry/react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import LoadingSpinner from "./layouts/loader/LoadingSpinner";
import { setIsMobileView } from "./redux/slice/viewSlice";
import { setIframeToken, refreshIframeToken, clearIframeToken } from "./redux/slice/guestAuthSlice";
import { fetchUserProfileRequest, clearUserProfileData } from "./redux/slice/userProfileSlice";
import { resetSessionExpired } from "./redux/slice/authSlice";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "./redux/rootReducer";
import { ChatSession, clearChatSessions } from "./redux/slice/chatSessionHistorySlice";
import { fetchChatMessagesRequest } from "./redux/slice/chatSlice";
import { iframeMessaging } from "@src/utils/iframeMessaging";
import { useNavigate } from "react-router-dom";
import { screens } from "./constants";
import { setSessionTitle } from "./redux/slice/renameTitleSlice";
import ChatRouteGuard from "./components/common/ChatRouteGuard";
import NetworkListener from "./components/common/NetworkListener";



// Lazy load components
const Profile = lazy(() => import("./layouts/profile/profile"));
const Sidebar = lazy(() => import("./layouts/home/SideMenu"));
const ChatBot = lazy(() => import("./layouts/chatbot/ChatWidget"));
const Login = lazy(() => import("./layouts/login/login"));
const Appbar = lazy(() => import("./layouts/home/Appbar"));


const SignupScreen = lazy(() => import("./layouts/signup/SignupScreen"));
const SettingScreen = lazy(() => import("./layouts/setting/SettingScreen"));
const AboutusScreen = lazy(() => import("./layouts/aboutus/AboutusScreen"));
const ProjectListScreen = lazy(() => import("./layouts/project/ProjectListScreen"));
const CreateProjectScreen = lazy(() => import("./layouts/project/CreateProjectScreen"));
const ContactusScreen = lazy(() => import("./layouts/contactus/ContactusScreen"));
const PrivacyPolicyScreen = lazy(() => import("./layouts/privacy/PrivacyPolicyScreen"));
const TermsOfUseScreen = lazy(() => import("./layouts/privacy/TermsOfUseScreen"));
const LaunchScreen = lazy(() => import("./layouts/launch/LaunchScreen"));
const WidgetTermsOfUseScreen = lazy(() => import("./layouts/launch/WidgetTermsOfUseScreen"));
const WidgetPrivacyPolicyScreen = lazy(() => import("./layouts/launch/WidgetPrivacyPolicyScreen"));
const WidgetAiSafetyScreen = lazy(() => import("./layouts/launch/WidgetAiSafetyScreen"));


// Force refresh of ChatWidget types
// const MarkdownTester = lazy(() => import("./components/common/MarkdownTester"));


const App: React.FC = () => {
    const dispatch = useDispatch(); // <--- initialize dispatch
    const [isMenuMinimized, setIsMenuMinimized] = useState(true);
    const [, setSelectedContent] = useState<
        "home" | "profile" | "settings" | "about" | "history" | "login"
    >("home");

    // Check if running in embed mode
    const [, setIsEmbedMode] = useState(false);

    // Initialize iframe state immediately to prevent layout shift
    const [isInIframe, ] = useState(() => {
        return iframeMessaging.isInIframe();
    });

    const { data } = useSelector((state: RootState) => state.profile);
    const [userType,setUserType] = useState("");
    useEffect(() => {
        if (data) {
            if (data.is_doctor) {
                setUserType("Clinician");
                //setUserType("Patient");
            }
            else {
                setUserType("Patient");
            }
            if (data.consents) {
                if (data.consents && Object.keys(data.consents).length === 0) {
                    localStorage.setItem("hasAcceptTerms", "true");
                }
                else {
                    if (data.consents.ORG_AI_USAGE?.status === "ACCEPTED") {
                        localStorage.setItem("hasAcceptTerms", "true");
                    }
                    else {
                        localStorage.setItem("hasAcceptTerms", "false");
                    }
                }
            }
        }
        }, [data]);

    // Initialize mobile state with better detection to prevent layout shift
    const [isMobileScreen, setIsMobileScreen] = useState(() => {
        const currentWidth = window.innerWidth;
        const inIframe = iframeMessaging.isInIframe();

        if (!inIframe) {
            // Standalone - use current width
            return currentWidth <= 768;
        }

        // In iframe - try to detect parent screen size
        try {
            // Try to access parent screen (might fail due to cross-origin)
            if (window.parent && window.parent.screen) {
                const parentScreenWidth = window.parent.screen.width;
                return parentScreenWidth <= 768;
            }
        } catch (e) {
            // Cross-origin restriction - fall back to conservative approach
        }

        return false;
    });

    const [, setParentWindowWidth] = useState<number>(window.innerWidth);

    useEffect(() => {
        dispatch(setSessionTitle(""));
        window.dispatchEvent(new Event("session-title-updated"));
        localStorage.setItem("isFirstVisit", "true")
    }, [dispatch]);

    useEffect(() => {
        setSelectedContent("home");
        localStorage.setItem("session_id", "");

        // Check for embed mode
        const urlParams = new URLSearchParams(window.location.search);
        const embedParam = urlParams.get("embed");
        setIsEmbedMode(embedParam === "true");

        // Mobile screen detection based on actual parent window width
        const updateMobileState = (width: number) => {
            const isMobile = width <= 768;
            setIsMobileScreen(isMobile);
        };

        if (isInIframe) {
            // In iframe - listen for parent dimensions via postMessage
            const handleParentDimensions = (event: MessageEvent) => {
                // Validate origin if needed (for security)
                if (event.data && event.data.type === "parent-window-dimensions") {
                    const parentWidth = event.data.width;
                    setParentWindowWidth(parentWidth);
                    updateMobileState(parentWidth);
                }
            };

            // Add message listener
            window.addEventListener("message", handleParentDimensions);

            // Request parent dimensions on startup (reduced delay to minimize layout shift)
            setTimeout(() => {
                window.parent.postMessage(
                    {
                        type: "request-parent-dimensions",
                        timestamp: Date.now(),
                    },
                    "*",
                );
            }, 100);

            return () => {
                window.removeEventListener("message", handleParentDimensions);
            };
        } else {
            // Standalone - use current window width
            const handleStandaloneResize = () => {
                const width = window.innerWidth;
                setParentWindowWidth(width);
                updateMobileState(width);
            };

            // Set initial value
            handleStandaloneResize();

            // Listen to current window resize
            window.addEventListener("resize", handleStandaloneResize);

            return () => {
                window.removeEventListener("resize", handleStandaloneResize);
            };
        }
    }, [isInIframe]);

    // Iframe token management - Handle token events at app level
    const iframeInitialized = React.useRef(false);
    const profileFetchDispatched = React.useRef(false);

    useEffect(() => {
        const iframeInfo = iframeMessaging.getIframeInfo();

        if (iframeInfo.isInIframe) {
            // CRITICAL FIX: Prevent double initialization in React StrictMode
            if (iframeInitialized.current) {
                return;
            }
            iframeInitialized.current = true;

            // Check for existing token in localStorage and sync with Redux
            const existingToken = localStorage.getItem("accessToken");
            if (existingToken) {
                // console.log("[App] Found existing token in localStorage, syncing with Redux");
                dispatch(setIframeToken({ accessToken: existingToken }));
                dispatch(fetchUserProfileRequest());
                profileFetchDispatched.current = true;
            }

            // Request token from parent on app start
            iframeMessaging.requestTokenFromParent();

            // Handle token received from parent
            const handleTokenReceived = (event: CustomEvent) => {
                const { token } = event.detail;
                dispatch(setIframeToken({ accessToken: token }));

                // Only dispatch once per session — iframeMessaging already called
                // fetchUserProfileApi directly before firing this event.
                if (!profileFetchDispatched.current) {
                    profileFetchDispatched.current = true;
                    dispatch(fetchUserProfileRequest());
                }
            };

            // Handle token refresh from parent
            const handleTokenRefreshed = (event: CustomEvent) => {
                const { token } = event.detail;
                dispatch(refreshIframeToken({ accessToken: token }));

                // Fetch profile again with refreshed token
                dispatch(fetchUserProfileRequest());
            };

            // Handle token errors
            const handleTokenError = (event: CustomEvent) => {
                const { error } = event.detail;
                console.error("[App] ❌ Iframe token error:", error);
                dispatch(clearIframeToken());
                dispatch(clearUserProfileData());
            };

            // Handle logout events
            const handleLogoutReceived = () => {
                console.log("[App] 🚪 Iframe logout received, clearing Redux state");
                profileFetchDispatched.current = false;
                dispatch(clearIframeToken());
                dispatch(clearUserProfileData());
            };

            // Add event listeners
            window.addEventListener("iframe-token-received", handleTokenReceived as EventListener);
            window.addEventListener("iframe-token-refreshed", handleTokenRefreshed as EventListener);
            window.addEventListener("iframe-token-error", handleTokenError as EventListener);
            window.addEventListener("iframe-logout-received", handleLogoutReceived);

            // CRITICAL FIX: Notify iframeMessaging that App is ready to receive events
            // This will flush any events that were queued before listeners were registered
            // console.log("[App] Event listeners registered, notifying iframeMessaging service");
            iframeMessaging.notifyAppReady();

            // Cleanup only resets the ref, doesn't remove listeners (persist across StrictMode mounts)
            return () => {
                // console.log("[App] Cleanup called, but keeping listeners active");
                // Don't remove listeners or reset ref to prevent StrictMode issues
            };
        }
    }, [dispatch]);

    useEffect(() => {
        const handleResize = () => {
            dispatch(setIsMobileView(window.innerWidth < 600)); // dispatching the state to redux
        };

        handleResize(); // run once on mount
        window.addEventListener("resize", handleResize); // subscribe to window size changes

        return () => window.removeEventListener("resize", handleResize); // cleanup on unmount
    }, [dispatch]);

    const isMobile = useSelector((state: RootState) => state.view.isMobileView);

    const handleMenuToggle = (minimized: boolean) => {
        setIsMenuMinimized(minimized);
    };

    const handleContentChange = (content: "home" | "profile" | "settings" | "about" | "history" | "login") => {
        setSelectedContent(content);
    };

    const [chatSession, setChatSession] = useState<ChatSession | null>(null);

    // Add a small delay to reduce header flashing
    const [showIframeHeaderDelayed, setShowIframeHeaderDelayed] = useState(false);

    // Iframe header should only show when: in iframe AND screen width ≤ 768px
    const showIframeHeader = isInIframe && isMobileScreen;

    // Use delayed state with smooth transition
    useEffect(() => {
        if (showIframeHeader) {
            // Show header after a small delay to reduce flashing
            const timer = setTimeout(() => {
                setShowIframeHeaderDelayed(true);
            }, 50);
            return () => clearTimeout(timer);
        } else {
            // Hide header immediately to prevent layout jumps
            setShowIframeHeaderDelayed(false);
        }
    }, [showIframeHeader]);

    // disbale right click in iframe mode
    useEffect(() => {
        if (!isInIframe) return;
    
        const disableRightClick = (event: MouseEvent) => {
            event.preventDefault();
        };
    
        document.addEventListener("contextmenu", disableRightClick);
    
        return () => {
            document.removeEventListener("contextmenu", disableRightClick);
        };
    }, [isInIframe]);
    

    const navigate = useNavigate();
    const isSessionExpired = useSelector(
        (state: RootState) => state.auth.isSessionExpired
      );
      
      useEffect(() => {
        if (isSessionExpired && !isInIframe) {
            dispatch(resetSessionExpired());
            localStorage.clear();
            sessionStorage.clear();
            dispatch({ type: "chat/clearMessages" });
            dispatch(clearUserProfileData());
            dispatch(clearChatSessions());
            navigate("/login", { replace: true });
        }
      }, [isSessionExpired, navigate, dispatch, isInIframe]);



      const location = useLocation();

      useEffect(() => {
        const normalizedPath = location.pathname.replace(/\/+$/, "") || "/";

        let screen = screens.home;
        
        if (normalizedPath === "/") screen = screens.home;
        else if (normalizedPath.startsWith("/chat")) screen = screens.home; // chat belongs to home
        else if (normalizedPath === "/profile") screen = screens.profile;
        else if (normalizedPath === "/settings") screen = screens.setting;
        else if (normalizedPath === "/about") screen = screens.about;
        else if (normalizedPath === "/privacy") screen = screens.privacy;
        else if (normalizedPath === "/contactus") screen = screens.contact;
        else if (normalizedPath === "/createproject") screen = screens.create_project;
        else if (normalizedPath === "/projectlist") screen = screens.list_project;
        else if (normalizedPath === "/login") screen = screens.login;
        else if (normalizedPath === "/launch") screen = screens.launch;
        
        // ✅ store using constants
        localStorage.setItem("current_screen", screen);
        
        // ✅ store chat session separately
        if (normalizedPath.startsWith("/chat")) {
          const chatId = normalizedPath.split("/chat/")[1];
        
          if (chatId) {
            localStorage.setItem("session_id", chatId);
          }
        }
      
      }, [location]);


      const hasAcceptTerms = localStorage.getItem("hasAcceptTerms");
      useEffect(() => {
        if (isInIframe) {
      
          if (hasAcceptTerms!=="true" && location.pathname === "/" && userType === "Patient") {
            navigate("/launch", { replace: true });
          }
        }
      }, [isInIframe, location.pathname, navigate, userType, hasAcceptTerms]);

    return (
        <>
            {/* Iframe header - only shows when screen is ≤768px and in iframe mode */}
            <div className="fixed z-[999] top-0"
                style={{
                    marginLeft: isMobile||isInIframe?"56px":isMenuMinimized ? "56px" : "300px",
                    width:
                    isMobile || isInIframe
                      ? "calc(100% - 50px)"
                      : isMenuMinimized
                      ? "calc(100% - 56px)"
                      : "calc(100% - 300px)",
              
                }}
            >
                {/* <div
                    className={`w-full transition-all duration-200 ease-in-out ${
                        showIframeHeaderDelayed ? "h-[50px] opacity-100" : "h-0 opacity-0"
                    }`}
                    style={{
                        overflow: "hidden",
                    }}
                >
                    {showIframeHeaderDelayed && <IframeHeader />}
                </div> */}
                <div
                    className="w-full h-[60px] transition-all duration-200 ease-in-out"
                    style={{
                        top: showIframeHeaderDelayed ? "50px" : "0px",
                    }}
                >
                    <Appbar isMinimized={isMenuMinimized} />
                </div>
            </div>

            <div
                className="app-container transition-all duration-200 ease-in-out"
                // style={{
                //     paddingTop: showIframeHeaderDelayed ? "50px" : "0px",
                // }}
            >
                <NetworkListener />
                <Suspense fallback={<LoadingSpinner />}>
                    <Sentry.ErrorBoundary fallback={<div>Something went wrong.</div>}>
                        <div className="h-screen w-full flex flex-col">
                            <div className="app-layout w-full flex flex-1">
                                <Sidebar
                                    onToggle={handleMenuToggle}
                                    isMenuMinimized={isMenuMinimized}
                                    isInIframe={isInIframe}
                                    onContentChange={handleContentChange}
                                    onSessionClick={(chat) => {
                                        localStorage.setItem("session_id", chat.id.toString());
                                        dispatch(fetchChatMessagesRequest({ session_id: chat.id }));
                                        setChatSession(chat); // this triggers WebSocket reconnect
                                        handleContentChange("home");
                                    }}
                                />
                                <div
                                    className={`main-content h-[calc(100% - 60px)] bg-[linear-gradient(0.36deg,_rgba(216,209,197,0.6)_0.33%,_rgba(173,228,228,0.2)_35.61%,_rgba(13,29,43,0.05)_99.71%)] flex-1  ${isMenuMinimized ? "minimized-content" : "expanded-content"}`}
                                >
                                    <Routes>

                                        {/* Launch Routes */}
                                        <Route path="/launch" element={
                                            (hasAcceptTerms!=="true" && userType === "Patient") ?
                                                <LaunchScreen />:
                                                <Navigate to="/" replace />
                                            } />

                                        {/* Public Routes */}
                                        <Route path="/login" element={<Login />} />
                                        <Route path="/signup" element={<SignupScreen />} />

                                        {/* Protected/Main Routes */}
                                        <Route
                                            path="/"
                                            element={
                                                <ChatBot
                                                    isMobile={isMobile}
                                                    chatSessionNew={chatSession}
                                                    isInIframe={isInIframe}
                                                />
                                            }
                                        />
                                        <Route
                                            path="/chat/:chatId"
                                            element={
                                                <ChatRouteGuard>
                                                    <ChatBot
                                                        isMobile={isMobile}
                                                        chatSessionNew={chatSession}
                                                        isInIframe={isInIframe}
                                                    />
                                                </ChatRouteGuard>
                                            }
                                        />
                                        {/* <Route path="/profile" element={<ProfileRedirect />} /> */}

                                        <Route
                                            path="/profile"
                                            element={
                                                localStorage.getItem("email") ? (
                                                    <Profile />
                                                ) : (
                                                    <Navigate to="/login" replace />
                                                )
                                            }
                                        />

                                        {/*<Route path="/settings" element={<SettingScreen/>} />*/}
                                        <Route
                                            path="/settings"
                                            element={
                                                // localStorage.getItem("email") ? (
                                                    <SettingScreen isInIframe={isInIframe}/>
                                                // ) : (
                                                //     <Navigate to="/about" replace />
                                                // )
                                            }
                                        />
                                        <Route path="/about" element={<AboutusScreen />} />
                                        <Route path="/history" element={<div>History Screen</div>} />
                                        <Route path="/projectlist" element={<ProjectListScreen />} />
                                        <Route path="/createproject" element={<CreateProjectScreen />} />
                                        <Route path="/contactus" element={<ContactusScreen />} />
                                        <Route path="/privacy" element={<PrivacyPolicyScreen />} />
                                        <Route path="/terms" element={<TermsOfUseScreen />} />
                                        <Route path="/patient_terms" element={<WidgetTermsOfUseScreen />} />
                                        <Route path="/patient_privacy" element={<WidgetPrivacyPolicyScreen/>}/>
                                        <Route path="/widget_ai" element={<WidgetAiSafetyScreen/>}/>
                                        

                                        {/* Development/Testing Routes */}
                                        {/* <Route path="/abcxyz/markdown-tester" element={<MarkdownTester />} /> */}
                                        {/*<div>Page Not Found</div>*/}
                                        <Route path="*" element={<Navigate to="/" replace />} />
                                    </Routes>
                                </div>
                            </div>
                        </div>
                    </Sentry.ErrorBoundary>
                </Suspense>
            </div>
        </>
    );
};

export default App;